/* eslint-disable jsx-a11y/anchor-is-valid */
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Genre as GenreDetail } from 'features/Genre/models/Genre';
import genreServiceDetail from 'features/Genre/pages/detail/services';
import { HIGH_LEVEL_ROW_BACKGROUND_COLOR } from 'features/MenuBook/constants';
import { menuBookEditorActions, selectMenuBookEditorExpandAll } from 'features/MenuBook/menuBookEditorSlice';
import { Genre } from 'features/MenuBook/models';
import React, { memo, useEffect, useState } from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { Form } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { MINUS_TEXT, PLUS_TEXT, PRIMARY_BUTTON } from 'utils';
import { wasMultiSelectKeyUsed, wasToggleInSelectionGroupKeyUsed } from '../../utils';
import GenreDetailModal from '../GenreAddModal/components/Detail';
import { COLUMN_WIDTH, getItemStyle } from '../MenuBookEditorModal';
export interface GenreRowCollapseProps {
  menuBookId?: number;
  genreId: number;
  children?: React.ReactNode;
  index: number;
  onSelectedRowChanged: () => void;
}

function GenreRow({ menuBookId, genreId, children, index, onSelectedRowChanged }: GenreRowCollapseProps) {
  const dispatch = useAppDispatch();

  const genre = useAppSelector((state) => {
    return state.menuBookEditor.data.find((genre) => genre.id === genreId);
  }) as Genre;

  const isExpand = useAppSelector(selectMenuBookEditorExpandAll);
  const [open, setOpen] = useState(true);

  // Genre Detail Modal
  const [showGenreDetailModal, setShowGenreDetailModal] = useState(false);
  const [genreModalDetailData, setGenreModalDetailData] = useState<{ genre: GenreDetail }>();

  const pathId = `${genre.id}`;
  const droppableId = `root`;
  const isSelected = false;
  const isGhosting = false;
  const mbEditorContentElement = document.getElementById('mb-editor-table');

  useEffect(() => {
    const btnExpandElem = document.querySelector(`#genre-${pathId}-row .dan-btn-expand`) as HTMLButtonElement;
    function handleBtnExpandElemClick(event: MouseEvent) {
      event.stopPropagation();
      setOpen(!open);
      btnExpandElem.innerHTML = !open ? MINUS_TEXT : PLUS_TEXT;
      btnExpandElem?.setAttribute('aria-expanded', !open ? 'true' : 'false');
    }

    if (btnExpandElem) {
      btnExpandElem.addEventListener('click', handleBtnExpandElemClick);
    }

    return () => {
      if (btnExpandElem) {
        btnExpandElem.removeEventListener('click', handleBtnExpandElemClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (isExpand !== undefined) {
      setOpen(isExpand);
    }
  }, [isExpand]);

  function onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) {
    if (event.defaultPrevented) {
      return;
    }

    if (snapshot.isDragging) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // we are using the event for selection
    event.preventDefault();

    performAction(event);
  }

  // Using onClick as it will be correctly
  // preventing if there was a drag
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    // marking the event as used
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    } else {
      return;
    }
  }

  function onTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    // marking the event as used
    // we would also need to add some extra logic to prevent the click
    // if this element was an anchor
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    }

    dispatch(menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: genre.id }));
  }

  function performAction(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      dispatch(menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: genre.id }));
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      dispatch(
        menuBookEditorActions.multiSelectTo({
          arg: {
            // data,
            // selectedItemIds,
            type: 'genre',
            droppableId: droppableId,
          },
          newItemId: genre.id,
        })
      );
      return;
    }

    dispatch(menuBookEditorActions.toggleSelection({ selectedDroppableId: droppableId, itemId: genre.id }));
  }

  async function handleOpenGenreDetailModal() {
    const response = await genreServiceDetail.getGenre(genre.id.toString());

    setShowGenreDetailModal(true);
    setGenreModalDetailData({ genre: response.data });
  }

  function handleCloseGenreDetailModal(status: boolean, data?: GenreDetail) {
    setShowGenreDetailModal(false);
    setGenreModalDetailData(undefined);
    if (status && data) {
      dispatch(menuBookEditorActions.updateGenre(data));
    }

    // focus on element with id="mb-editor-table-safe-focus"
    requestAnimationFrame(() => {
      const mbEditorContentElement = document.getElementById('mb-editor-table-safe-focus');
      mbEditorContentElement?.focus();
    });
  }

  return (
    <>
      <Draggable draggableId={`genre-${pathId}`} index={index}>
        {(provided, snapshot) => {
          return (
            <>
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={getItemStyle(
                  snapshot.isDragging,
                  provided.draggableProps.style,
                  isSelected,
                  isGhosting,
                  HIGH_LEVEL_ROW_BACKGROUND_COLOR
                )}
                onClick={onClick}
                onTouchEnd={onTouchEnd}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event, provided, snapshot)}
              >
                <>
                  <div id={`genre-${pathId}-row`} className="dan-tr divide">
                    <div className="dan-td d-flex justify-content-center" style={{ width: `${COLUMN_WIDTH[0]}%` }}>
                      <Form.Check
                        className="chk-mbeditor-item custom-checkbox"
                        data-type="genre"
                        data-genre_name={genre.name}
                        // data-menu_setting_genre_id={genre.menu_setting_genre_id}
                        data-subgenre_ids={genre.subgenres.map((subgenre) => subgenre.id).join(',')}
                        data-deletetype={menuBookId ? 'menubook_genres' : 'course_genres'}
                        defaultValue={genre.id}
                        tabIndex={-1}
                        onChange={onSelectedRowChanged}
                      ></Form.Check>
                    </div>
                    <div
                      className="dan-td"
                      style={{
                        width: `${COLUMN_WIDTH.slice(1, 10).reduce((sum, width) => sum + width, 0)}%`,
                      }}
                    >
                      <button
                        className="dan-btn-expand"
                        tabIndex={-1}
                        aria-controls={`genre-${pathId}-content`}
                        aria-expanded={false}
                      >
                        {open ? MINUS_TEXT : PLUS_TEXT}
                      </button>
                      <a
                        // variant="link"
                        className="px-0 btn btn-link"
                        style={{ minWidth: 'inherit' }}
                        onClick={handleOpenGenreDetailModal}
                        rel="noreferrer"
                        tabIndex={-1}
                      >
                        {genre.name}
                      </a>
                    </div>
                  </div>
                  <div style={{ display: open ? 'block' : 'none' }}>
                    <div id={`genre-${pathId}-content`}>{children}</div>
                  </div>
                </>
              </div>
            </>
          );
        }}
      </Draggable>

      {showGenreDetailModal &&
        genreModalDetailData &&
        createPortal(
          <GenreDetailModal
            genre={genreModalDetailData.genre}
            show={showGenreDetailModal}
            handleCloseModalEdit={handleCloseGenreDetailModal}
          />,
          document.body
        )}
    </>
  );
}

export default memo(GenreRow);
